<script setup>
import axios from 'axios';
import { onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import PgeButton from '../../../../pge-components/pge-button.vue';
import PgeInputText from '../../../../pge-components/pge-input-text.vue';
import RequestAccessModal from '../RequestAccessModal.vue';
import AccessBlockedNotice from '../AccessBlockedNotice.vue';
import AccessRequestPendingNotice from '../AccessRequestPendingNotice.vue';
import AuthTermsNotice from '../AuthTermsNotice.vue';
import GoogleAuthButton from '../GoogleAuthButton.vue';

const props = defineProps({
	eventData: Object,
	changeFormStep: Function,
	userData: Object,
	setUserData: Function,
});
const isLoading = ref(false);
const emailInput = ref(false);
const emailLocked = ref(false);
const errors = ref([]);
const hasChanged = ref(false);
const hasError = ref(false);
const accessBlockedMessage = ref('');
const pendingRequest = ref(false);
const requestAccessOpen = ref(false);
const router = useRouter();
watch(emailInput, newVal => {
	hasChanged.value = newVal;
	hasError.value = !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(newVal);
	// Clear a prior access-block so a different email can be tried.
	accessBlockedMessage.value = '';
	pendingRequest.value = false;
});

function lookupFailureLine(status) {
	if (status === 404) return null;
	if (status === 422) return 'Invalid email address';
	return 'We could not check that email right now. Please try again in a moment.';
}

// Pre-screen Hub access up front so a blocked user is told before entering a
// password, not after "logging in". Existing accounts pass their roles (from
// user-verify) so tenant admins — who are exempt at the authoritative login
// gate — aren't stopped here.
async function passesAccessGate(roles = []) {
	try {
		const { data: validation } = await axios.post('/api/access/validate-email', { email: emailInput.value, roles });
		if (validation?.data?.pending_request) {
			pendingRequest.value = true;
			return false;
		}
		if (validation?.data?.allowed === false) {
			accessBlockedMessage.value = 'You do not currently have access to this Hub. If you believe you should have access, please submit an access request.';
			return false;
		}
	} catch (e) {
		// validate-email failed — fail open, proceed with normal registration
		console.error('validate-email check failed', e);
	}
	return true;
}

// SSO answers 403 EMAIL_NOT_VERIFIED for an unverified account; signup would mail no code for it, so resend one here.
async function resumeVerification() {
	try {
		await axios.post('/api/sso/api/email/resend-code', {
			email: emailInput.value,
			subdomain: window.store?.subdomain,
			logo: props.eventData?.login_logo,
		});
	} catch {
		errors.value = ['We could not send a new verification code. Please try again in a moment.'];
		return;
	}

	props.changeFormStep('verify-code', { email: emailInput.value });
}

// Existing members of this hub, admins included (their authority is a per-hub role the
// pre-screen's global roles can't see), go on to auth and the post-login gate decides; only non-members are pre-screened.
async function handleExistingUser(data) {
	const memberOfThisHub = Array.isArray(data.data?.subdomains)
		&& data.data.subdomains.includes(window.store.subdomain);
	if (!memberOfThisHub && !(await passesAccessGate(data.data?.roles || []))) return;
	props.changeFormStep("confirm-password", { user: data.data, memberOfThisHub }, true);
}

async function startNewAccount() {
	if (!(await passesAccessGate())) return;
	props.changeFormStep("account-details", { user: { email: emailInput.value } });
}

async function handleLookupFound({ data }) {
	if (data.user_deactivated || data.user_returned) {
		props.changeFormStep("user-already-exist", data);
	} else if (data.success) {
		await handleExistingUser(data);
	} else {
		await startNewAccount();
	}
}

async function handleLookupFailed(res) {
	if (res.response?.data?.error_code === 'EMAIL_NOT_VERIFIED') {
		if (!(await passesAccessGate())) return;
		await resumeVerification();
		return;
	}

	const failureLine = lookupFailureLine(res.response?.status);
	if (failureLine) {
		errors.value = [failureLine];
		return;
	}

	await startNewAccount();
}

function checkEmail() {
	if(!emailInput.value || hasError.value) return;

	isLoading.value = true;
	accessBlockedMessage.value = '';
	pendingRequest.value = false;
	errors.value = [];

	// POST keeps the address out of the URL, and so out of access logs and
	// traces (XN-4886). The hub's proxy adds this hub's name.
	axios.post('/api/sso/api/user-verify', { email: emailInput.value })
		.then(handleLookupFound)
		.catch(handleLookupFailed)
		.finally(() => {
			isLoading.value = false;
		});
}
function resetBlockedState() {
	emailInput.value = '';
	emailLocked.value = false;
	accessBlockedMessage.value = '';
	pendingRequest.value = false;
	hasChanged.value = false;
	hasError.value = false;
	errors.value = [];
	const query = { ...router.currentRoute.value.query };
	delete query.access_blocked;
	delete query.pending_request;
	delete query.email;
	router.replace({ query });
}
function handleCancel() {
	if (pendingRequest.value || accessBlockedMessage.value) {
		resetBlockedState();
		return;
	}
	router.push("/");
}

onMounted(() => {
	const params = new URLSearchParams(window.location.search);
	// Surfaced when Google sign-in was blocked by hub access management.
	if (params.get('access_blocked')) {
		accessBlockedMessage.value = 'You do not currently have access to this Hub. If you believe you should have access, please submit an access request.';
	}
	// Google sign-in whose email already has a pending access request.
	if (params.get('pending_request')) {
		pendingRequest.value = true;
	}
	// Arriving from an invitation link (/register?email=…): prefill and lock the
	// email — the invitation is bound to this address.
	const invitedEmail = params.get('email');
	if (invitedEmail) {
		emailInput.value = invitedEmail;
		emailLocked.value = true;
	}
});
</script>

<template>
	<div class="form">
		<div class="form__title">
			Log in or register
		</div>
		<!-- Google sign-in leads, then "or", then the email form. Hidden while a
		     request is pending or the hub's access list blocks this email, matching
		     how "Continue with Email" is suppressed — Google wouldn't bypass the gate
		     (the callback re-checks it), so offering it there would only mislead. -->
		<template v-if="!pendingRequest && !accessBlockedMessage">
			<GoogleAuthButton />
			<div class="form__divider">
				<span class="form__divider__line"></span>
				<span class="form__divider__text">or</span>
				<span class="form__divider__line"></span>
			</div>
		</template>
		<div class="bg-white">
			<pge-input-text type="email" name="email" label="Email Address" :model="emailInput" v-model="emailInput"
				:submit-on-enter="checkEmail" :disabled="emailLocked" required has-required-sign should-show-error no-tabindex />
		</div>
		<AccessRequestPendingNotice v-if="pendingRequest" />
		<AccessBlockedNotice v-else-if="accessBlockedMessage" @request="requestAccessOpen = true" />
		<div class="form__actions" v-if="pendingRequest" style="justify-content: center;">
			<pge-button label="Close" @click="handleCancel" />
		</div>
		<div class="form__actions" v-else>
			<pge-button label="Close" @click="handleCancel" template="text" />
			<pge-button v-if="!accessBlockedMessage" label="Continue with Email" @click="checkEmail"
				:disabled="!hasChanged || !emailInput || hasError || isLoading" />
			<pge-button v-else label="Request Access" @click="requestAccessOpen = true" />
		</div>
		<div class="form__errors" v-if="errors.length">
			<div v-for="(error, index) in errors" :key="index">{{ error }}</div>
		</div>
		<AuthTermsNotice v-if="!pendingRequest" :links="eventData?.settings?.links || []" />

		<RequestAccessModal :visible="requestAccessOpen" :initial-email="emailInput || ''" :event-data="eventData" @close="requestAccessOpen = false" />
	</div>
</template>

<style lang="scss" scoped>
.form {
	display: flex;
	flex-direction: column;
	gap: 22px;

	&__title {
		color: #000 !important;
		font-feature-settings: 'liga' off, 'clig' off !important;
		font-family: Lato, sans-serif !important;
		font-size: 18px !important;
		font-style: normal !important;
		font-weight: 700 !important;
		line-height: normal !important;
		text-align: center;
	}

	&__divider {
		display: flex;
		align-items: center;
		gap: 12px;
		width: 100%;

		&__line {
			flex: 1;
			height: 1px;
			background: #E0E0E0;
		}

		&__text {
			color: #7D747E;
			font-size: 14px;
		}
	}

	&__actions {
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;
	}

	&__errors {
		display: flex;
		flex-direction: column;
		gap: 10px;

		div {
			text-align: center;
			color: #f83c3c;
			font-weight: 700;
			font-size: 14px;
			line-height: 20px;
		}
	}

}

.bg-white {
	background: white;
}
</style>
